import { useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { MoveAttachmentsLog, MoveAttachmentsLogAction, SourceSystem } from '@atlas/shared';
import { SOURCE_SYSTEM_LABELS } from '@atlas/shared';
import { subscribeToMoveAttachmentLogs } from '../lib/moveAttachments.js';
import { SourceBadge } from './SourceBadge.js';

const ACTIONS: Record<MoveAttachmentsLogAction, { label: string; className: string }> = {
  removed_from_history: { label: 'Removed from history', className: 'bg-[#f1f1ef] text-[#73726e]' },
  deleted_from_s3: { label: 'Deleted from S3', className: 'bg-[#fbe8e8] text-[#e03e3e]' },
  s3_delete_failed: { label: 'Delete failed', className: 'bg-[#fbf3db] text-[#b5820b]' },
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function sourceLabel(sourceSystem: string): string {
  return SOURCE_SYSTEM_LABELS[sourceSystem as SourceSystem] ?? sourceSystem;
}

/** Clarifies what a history-only removal did (and didn't) touch in S3. */
function removalNote(copied: boolean | undefined): string {
  if (copied === true) {
    return 'Only the history record was removed — the copied files remain in S3.';
  }
  if (copied === false) {
    return 'Only the history record was removed — nothing had been copied to S3.';
  }
  return 'Only the history record was removed — files in S3 (if any) were left in place.';
}

export function MoveAttachmentsLog() {
  const [logs, setLogs] = useState<Array<MoveAttachmentsLog & { id: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    return subscribeToMoveAttachmentLogs(setLogs, (err) => setError(err.message));
  }, []);

  const needle = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!needle) return logs;
    return logs.filter((entry) =>
      [
        entry.firm,
        entry.sourceSystem,
        sourceLabel(entry.sourceSystem),
        entry.actor?.displayName,
        entry.actor?.email,
      ].some((value) => typeof value === 'string' && value.toLowerCase().includes(needle)),
    );
  }, [logs, needle]);

  return (
    <details className="group mt-8 rounded-lg border border-[#e9e9e7] bg-white">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-xs font-medium text-[#73726e] [&::-webkit-details-marker]:hidden">
        <ChevronRight
          size={13}
          className="shrink-0 text-[#aeaaa4] transition-transform group-open:rotate-90"
        />
        Deleted logs{logs.length > 0 ? ` · ${logs.length}` : ''}
      </summary>
      <div className="border-t border-[#f1f1ef] px-3 pb-1">
        {error && <p className="py-2 text-xs text-[#e03e3e]">{error}</p>}
        {!error && logs.length === 0 && (
          <p className="py-3 text-xs text-[#aeaaa4]">No admin actions yet.</p>
        )}
        {!error && logs.length > 0 && (
          <div className="flex items-center justify-between gap-3 py-2">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by agency, source, or deleted by…"
              className="w-full sm:w-96 rounded-md border border-[#e9e9e7] bg-white px-3 py-1.5 text-sm text-[#37352f] placeholder:text-[#aeaaa4] focus:border-[#2383e2] focus:outline-none"
            />
            {needle && (
              <span className="shrink-0 text-xs tabular-nums text-[#73726e]">
                {filtered.length} of {logs.length}
              </span>
            )}
          </div>
        )}
        {!error && logs.length > 0 && filtered.length === 0 && (
          <p className="py-3 text-xs text-[#aeaaa4]">No logs match “{search.trim()}”.</p>
        )}
        {filtered.map((entry) => {
          const action = ACTIONS[entry.action];
          const who = entry.actor?.displayName || entry.actor?.email || 'An admin';
          const note = entry.action === 'removed_from_history' ? removalNote(entry.copied) : null;
          return (
            <article key={entry.id} className="border-t border-[#f1f1ef] py-2.5 first:border-t-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <span
                    className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${action.className}`}
                  >
                    {action.label}
                  </span>
                  <span className="font-mono text-sm text-[#37352f]">{entry.firm}</span>
                  <SourceBadge system={entry.sourceSystem} />
                  {entry.copied !== undefined && (
                    <span
                      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                        entry.copied
                          ? 'bg-[#e5f5f2] text-[#0f7b6c]'
                          : 'bg-[#f1f1ef] text-[#73726e]'
                      }`}
                    >
                      {entry.copied ? 'Files copied' : 'Dry run only'}
                    </span>
                  )}
                  {entry.action === 'deleted_from_s3' && entry.deleted != null && (
                    <span className="text-xs tabular-nums text-[#73726e]">
                      {entry.deleted.toLocaleString('en-GB')} objects
                    </span>
                  )}
                </div>
                <time className="shrink-0 text-xs text-[#aeaaa4]" dateTime={entry.at}>
                  {formatWhen(entry.at)}
                </time>
              </div>
              <p className="mt-1 text-xs text-[#73726e]">{who}</p>
              {note && <p className="mt-0.5 text-xs text-[#aeaaa4]">{note}</p>}
              {entry.prefixes.length > 0 && (
                <details className="group/paths mt-1.5">
                  <summary className="flex w-fit cursor-pointer list-none items-center gap-1 text-xs text-[#aeaaa4] [&::-webkit-details-marker]:hidden">
                    <ChevronRight
                      size={12}
                      className="shrink-0 transition-transform group-open/paths:rotate-90"
                    />
                    {entry.prefixes.length} destination{entry.prefixes.length === 1 ? '' : 's'}
                  </summary>
                  <ul className="mt-1 flex flex-col gap-0.5 pl-4">
                    {entry.prefixes.map((uri) => (
                      <li key={uri} className="break-all font-mono text-[11px] leading-5 text-[#37352f]">
                        {uri}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {entry.error && <p className="mt-1 text-xs text-[#e03e3e]">{entry.error}</p>}
            </article>
          );
        })}
      </div>
    </details>
  );
}
